import { useState, useEffect, useCallback } from 'react';
import { getAgentsConfig, saveAgentConfig, getReposStatus, indexRepo } from '../lib/api';
import type { AgentConfig, RepoStatus } from '../lib/api';
import styles from './AgentConfigPage.module.css';

const AGENTS = [
  { id: 'oski', label: 'Oski', color: 'var(--broota)',   desc: 'Orquestador — decide quién responde' },
  { id: 'pame', label: 'Pame', color: 'var(--nalca)',    desc: 'Producto/Dev — analiza código y estructura pedidas' },
  { id: 'neo',  label: 'Neo',  color: 'var(--xbroota)',  desc: 'Implementación — crea branches, commits y PRs en Nalca' },
  { id: 'kika', label: 'Kika', color: 'var(--dully)',    desc: 'QA — revisa los cambios de Neo antes de commitear' },
] as const;

const MODELS = [
  { value: 'claude-haiku-4-5-20251001', label: 'Haiku 4.5 — rápido, económico' },
  { value: 'claude-sonnet-4-6',         label: 'Sonnet 4.6 — equilibrado (recomendado)' },
  { value: 'claude-opus-4-7',           label: 'Opus 4.7 — máxima calidad' },
];

const LANGUAGES = [
  { value: 'es', label: 'Español' },
  { value: 'en', label: 'English' },
];

type AgentId = 'oski' | 'pame' | 'neo' | 'kika';

export function AgentConfigPage() {
  const [activeTab, setActiveTab]   = useState<AgentId>('oski');
  const [configs, setConfigs]       = useState<Record<string, AgentConfig>>({});
  const [defaults, setDefaults]     = useState<Record<string, AgentConfig>>({});
  const [draft, setDraft]           = useState<Partial<AgentConfig>>({});
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [error, setError]           = useState('');
  const [repoInput, setRepoInput]         = useState('');
  const [testRepoInput, setTestRepoInput] = useState('');
  const [repoStatuses, setRepoStatuses]   = useState<Record<string, RepoStatus>>({});
  const [indexing, setIndexing]           = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { config, defaults: def } = await getAgentsConfig();
      setConfigs(config);
      setDefaults(def);
      setDraft(config[activeTab] ?? {});

      // Load repo statuses for all repos across pame + neo + kika
      const allRepos = [...new Set([
        ...(config.pame?.repos ?? def.pame?.repos ?? []),
        ...(config.neo?.repos  ?? def.neo?.repos  ?? []),
        ...(config.kika?.repos ?? def.kika?.repos ?? []),
      ])];
      if (allRepos.length) {
        getReposStatus(allRepos)
          .then(statuses => {
            const map: Record<string, RepoStatus> = {};
            statuses.forEach(s => { map[s.repo] = s; });
            setRepoStatuses(map);
          })
          .catch(() => {});
      }
    } catch {
      setError('No se pudo cargar la configuración.');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { load(); }, [load]);

  // Detectar cambios no guardados comparando draft vs config guardada
  const isDirty = JSON.stringify(draft) !== JSON.stringify(configs[activeTab] ?? {});

  // Advertir antes de cambiar de tab con cambios no guardados
  const switchTab = (id: AgentId) => {
    if (isDirty && !window.confirm('Tienes cambios sin guardar. ¿Cambiar de agente y perderlos?')) return;
    setActiveTab(id);
    setDraft(configs[id] ?? {});
    setSaved(false);
    setError('');
  };

  // Advertir antes de cerrar/navegar con cambios no guardados
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const set = (key: keyof AgentConfig, value: unknown) =>
    setDraft(d => ({ ...d, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      await saveAgentConfig(activeTab, draft);
      setConfigs(c => ({ ...c, [activeTab]: draft as AgentConfig }));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Error al guardar. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const def = defaults[activeTab] ?? {};
    // Preservar repos — el usuario los gestiona manualmente, no son parte del "default"
    setDraft({ ...def, repos: draft.repos });
    setSaved(false);
  };

  const addRepo = () => {
    const r = repoInput.trim();
    if (!r || (draft.repos ?? []).includes(r)) return;
    set('repos', [...(draft.repos ?? []), r]);
    setRepoInput('');
  };

  const removeRepo = (repo: string) =>
    set('repos', (draft.repos ?? []).filter(r => r !== repo));

  const addTestRepo = () => {
    const r = testRepoInput.trim();
    if (!r || (draft.testRepos ?? []).includes(r)) return;
    set('testRepos', [...(draft.testRepos ?? []), r]);
    setTestRepoInput('');
  };

  const removeTestRepo = (repo: string) =>
    set('testRepos', (draft.testRepos ?? []).filter(r => r !== repo));

  const handleReindex = async (repo: string) => {
    setIndexing(prev => ({ ...prev, [repo]: true }));
    try {
      const result = await indexRepo(repo);
      setRepoStatuses(prev => ({
        ...prev,
        [repo]: {
          ...prev[repo],
          repo,
          indexed: true,
          treeSize: result.treeSize,
          lastIndexedAt: new Date().toISOString(),
        } as RepoStatus,
      }));
    } catch {
      // silently fail — user sees no change
    } finally {
      setIndexing(prev => ({ ...prev, [repo]: false }));
    }
  };

  if (loading) return <div className={styles.loading}>Cargando configuración...</div>;

  const agent = AGENTS.find(a => a.id === activeTab)!;

  return (

    <div className={styles.page}>
      {/* Tabs */}
      <div className={styles.tabs}>
        {AGENTS.map(a => (
          <button
            key={a.id}
            className={`${styles.tab} ${activeTab === a.id ? styles.tabActive : ''}`}
            style={{ '--agent-color': a.color } as React.CSSProperties}
            onClick={() => switchTab(a.id as AgentId)}>
            <span className={styles.tabDot} style={{ background: a.color }} />
            {a.label}
          </button>
        ))}
      </div>

      {/* Header del agente */}
      <div className={styles.agentHeader}>
        <div className={styles.agentBadge} style={{ background: agent.color }}>
          {agent.label[0]}
        </div>
        <div>
          <div className={styles.agentTitle}>{draft.name ?? agent.label}</div>
          <div className={styles.agentDesc}>{agent.desc}</div>
        </div>
      </div>

      <div className={styles.grid}>
        {/* ── Columna izquierda ── */}
        <div className={styles.col}>

          {/* Identidad */}
          <section className={styles.card}>
            <div className={styles.cardTitle}>Identidad</div>
            <div className={styles.fieldRow}>
              <label className={styles.label}>Nombre</label>
              <input
                className={styles.input}
                value={draft.name ?? ''}
                onChange={e => set('name', e.target.value)}
                placeholder="Ej: Oski"
              />
            </div>
            <div className={styles.fieldRow}>
              <label className={styles.label}>Rol visible</label>
              <input
                className={styles.input}
                value={draft.role ?? ''}
                onChange={e => set('role', e.target.value)}
                placeholder="Ej: Orquestador de equipo"
              />
            </div>
            <div className={styles.fieldRow}>
              <label className={styles.label}>Idioma por defecto</label>
              <select
                className={styles.select}
                value={draft.language ?? 'es'}
                onChange={e => set('language', e.target.value)}>
                {LANGUAGES.map(l => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
            </div>
          </section>

          {/* System Prompt */}
          <section className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>System Prompt</div>
              <span className={styles.cardHint}>Instrucciones base del agente</span>
            </div>
            <textarea
              className={styles.textarea}
              rows={14}
              value={draft.systemPrompt ?? ''}
              onChange={e => set('systemPrompt', e.target.value)}
              spellCheck={false}
            />
          </section>

          {/* Memoria */}
          <section className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>Memoria del equipo</div>
              <span className={styles.cardHint}>Contexto inyectado en cada conversación</span>
            </div>
            <textarea
              className={styles.textarea}
              rows={6}
              value={draft.memory ?? ''}
              onChange={e => set('memory', e.target.value)}
              placeholder={`Ejemplos:
- Sprint actual: migración de autenticación en Nalca
- Stack preferido para nuevas features: usar los mismos patterns ya existentes en el repo
- Equipo dev: JAB (fullstack), ...
- Convención: siempre usar pnpm, no npm`}
            />
          </section>
        </div>

        {/* ── Columna derecha ── */}
        <div className={styles.col}>

          {/* Modelo y parámetros */}
          <section className={styles.card}>
            <div className={styles.cardTitle}>Modelo y parámetros</div>

            {activeTab === 'neo' ? (
              <>
                <div className={styles.fieldRow}>
                  <label className={styles.label}>
                    Modelo de planificación
                    <span className={styles.labelHint}>Exploración, análisis, CI repair</span>
                  </label>
                  <ModelSelect
                    value={draft.planModel ?? 'claude-sonnet-4-6'}
                    onChange={v => set('planModel', v)}
                    styles={styles}
                  />
                </div>
                <div className={styles.fieldRow}>
                  <label className={styles.label}>
                    Modelo de código
                    <span className={styles.labelHint}>Implementación, commits, PRs</span>
                  </label>
                  <ModelSelect
                    value={draft.model ?? 'claude-opus-4-7'}
                    onChange={v => set('model', v)}
                    styles={styles}
                  />
                </div>
              </>
            ) : (
              <div className={styles.fieldRow}>
                <label className={styles.label}>Modelo</label>
                <ModelSelect
                  value={draft.model ?? 'claude-sonnet-4-6'}
                  onChange={v => set('model', v)}
                  styles={styles}
                />
              </div>
            )}

            <div className={styles.fieldRow}>
              <label className={styles.label}>
                Temperatura
                <span className={styles.labelHint}>
                  {draft.temperature ?? 0.7} — {tempLabel(draft.temperature ?? 0.7)}
                </span>
              </label>
              <input
                type="range" min={0} max={1} step={0.05}
                className={styles.range}
                value={draft.temperature ?? 0.7}
                onChange={e => set('temperature', parseFloat(e.target.value))}
              />
              <div className={styles.rangeLabels}>
                <span>Preciso</span><span>Creativo</span>
              </div>
            </div>

            <div className={styles.fieldRow}>
              <label className={styles.label}>
                Max tokens
                <span className={styles.labelHint}>Largo máximo de respuesta</span>
              </label>
              <input
                type="number" min={256} max={8192} step={256}
                className={styles.input}
                value={draft.maxTokens ?? 1024}
                onChange={e => set('maxTokens', parseInt(e.target.value))}
              />
            </div>
          </section>

          {/* Repos (Pame, Neo, Kika) */}
          {(activeTab === 'pame' || activeTab === 'neo' || activeTab === 'kika') && (
            <section className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.cardTitle}>Repositorios GitHub</div>
                <span className={styles.cardHint}>
                  {activeTab === 'pame' && 'Repos que Pame puede leer para dar contexto'}
                  {activeTab === 'neo'  && 'Repos que Neo puede modificar'}
                  {activeTab === 'kika' && 'Repos cuyos cambios Kika puede revisar'}
                </span>
              </div>
              <div className={styles.repoList}>
                {(draft.repos ?? []).map(repo => {
                  const st = repoStatuses[repo];
                  return (
                    <div key={repo} className={styles.repoItem}>
                      <div className={styles.repoMain}>
                        <span className={styles.repoName}>{repo.split('/')[1]}</span>
                        <span className={styles.repoOrg}>{repo.split('/')[0]}</span>
                      </div>
                      <div className={styles.repoMeta}>
                        {/* Indexed badge */}
                        {st ? (
                          <span className={`${styles.repoBadge} ${st.indexed ? styles.repoBadgeOk : styles.repoBadgeWarn}`}
                            title={st.indexed ? `Última indexación: ${st.lastIndexedAt ?? '—'}` : 'Repo no indexado — click ↻ para indexar'}>
                            {st.indexed ? `✓ ${st.treeSize} archivos` : '⚠ sin índice'}
                          </span>
                        ) : (
                          <span className={`${styles.repoBadge} ${styles.repoBadgeMuted}`}>…</span>
                        )}
                        {/* Pame access */}
                        {st?.pame && (
                          <span className={`${styles.repoAccess} ${st.pame.access === 'ok' ? styles.repoAccessRead : styles.repoAccessNone}`}
                            title={`Pame — token: GITHUB_TOKEN${st.pame.access !== 'ok' ? '\nSin acceso: el token no tiene permisos sobre este repo' : ''}`}>
                            <span className={styles.repoAccessAgent}>Pame</span>
                            <span className={styles.repoAccessPerm}>
                              {st.pame.access === 'ok' ? '👁 read' : '✕'}
                            </span>
                          </span>
                        )}
                        {/* Neo access */}
                        {st?.neo && (
                          <span className={`${styles.repoAccess} ${st.neo.access === 'ok' ? (st.neo.permission === 'read-write' ? styles.repoAccessWrite : styles.repoAccessRead) : styles.repoAccessNone}`}
                            title={`Neo — token: GITHUB_NEO_TOKEN${st.neo.access !== 'ok' ? '\nSin acceso: el token no tiene permisos sobre este repo' : ''}`}>
                            <span className={styles.repoAccessAgent}>Neo</span>
                            <span className={styles.repoAccessPerm}>
                              {st.neo.access === 'ok' ? (st.neo.permission === 'read-write' ? '✎ read-write' : '👁 read') : '✕'}
                            </span>
                          </span>
                        )}
                        {/* Reindex button */}
                        <button
                          className={styles.repoReindex}
                          onClick={() => handleReindex(repo)}
                          disabled={indexing[repo]}
                          title="Reindexar ahora">
                          {indexing[repo] ? '⟳' : '↻'}
                        </button>
                      </div>
                      <button
                        className={styles.repoRemove}
                        onClick={() => removeRepo(repo)}
                        aria-label={`Quitar ${repo}`}>
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className={styles.repoAdd}>
                <input
                  className={styles.input}
                  value={repoInput}
                  onChange={e => setRepoInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addRepo()}
                  placeholder="owner/repo"
                />
                <button className={styles.addBtn} onClick={addRepo}>+</button>
              </div>
            </section>
          )}

          {/* Test generation (Neo only) */}
          {activeTab === 'neo' && (
            <section className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.cardTitle}>Generación de tests</div>
                <span className={styles.cardHint}>
                  Repos donde Neo escribe tests junto al código en cada PR
                </span>
              </div>
              <div className={styles.repoList}>
                {(draft.testRepos ?? []).length === 0 && (
                  <div className={styles.repoEmpty}>Sin repos habilitados — Neo no generará tests</div>
                )}
                {(draft.testRepos ?? []).map(repo => (
                  <div key={repo} className={styles.repoItem}>
                    <div className={styles.repoMain}>
                      <span className={styles.repoName}>{repo.split('/')[1]}</span>
                      <span className={styles.repoOrg}>{repo.split('/')[0]}</span>
                    </div>
                    <div className={styles.repoMeta}>
                      <span className={`${styles.repoBadge} ${styles.repoBadgeOk}`}>✓ tests activos</span>
                    </div>
                    <button
                      className={styles.repoRemove}
                      onClick={() => removeTestRepo(repo)}
                      aria-label={`Quitar ${repo}`}>
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <div className={styles.repoAdd}>
                <input
                  className={styles.input}
                  value={testRepoInput}
                  onChange={e => setTestRepoInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addTestRepo()}
                  placeholder="owner/repo"
                />
                <button className={styles.addBtn} onClick={addTestRepo}>+</button>
              </div>
            </section>
          )}

          {/* Referencia rápida */}
          <section className={styles.card}>
            <div className={styles.cardTitle}>Referencia rápida</div>
            <div className={styles.refList}>
              <div className={styles.refItem}>
                <span className={styles.refKey}>Temperatura 0.0–0.3</span>
                <span className={styles.refVal}>Respuestas precisas y deterministas. Ideal para Dully (legal) y Pame (código).</span>
              </div>
              <div className={styles.refItem}>
                <span className={styles.refKey}>Temperatura 0.5–0.8</span>
                <span className={styles.refVal}>Balance creatividad/coherencia. Ideal para Oski (conversacional).</span>
              </div>
              <div className={styles.refItem}>
                <span className={styles.refKey}>Memoria del equipo</span>
                <span className={styles.refVal}>Se inyecta al inicio de cada conversación, antes del mensaje del usuario. Úsala para contexto operacional que cambia frecuentemente.</span>
              </div>
              <div className={styles.refItem}>
                <span className={styles.refKey}>System Prompt</span>
                <span className={styles.refVal}>Define quién es el agente y cómo razona. Cambios aquí impactan el comportamiento base.</span>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Footer con acciones */}
      <div className={styles.footer}>
        {error && <span className={styles.errorMsg}>{error}</span>}
        {saved && <span className={styles.savedMsg}>✓ Guardado — activo en ~60s</span>}
        <div className={styles.actions}>
          <button className={styles.resetBtn} onClick={handleReset} disabled={saving}>
            Restaurar defaults
          </button>
          <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : `Guardar cambios en ${agent.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function tempLabel(t: number) {
  if (t <= 0.2) return 'muy preciso';
  if (t <= 0.4) return 'preciso';
  if (t <= 0.6) return 'equilibrado';
  if (t <= 0.8) return 'creativo';
  return 'muy creativo';
}

const CUSTOM_MODEL = '__custom__';

function ModelSelect({ value, onChange, styles }: {
  value: string;
  onChange: (v: string) => void;
  styles: Record<string, string>;
}) {
  const isKnown = MODELS.some(m => m.value === value);

  const handleSelect = (v: string) => {
    if (v === CUSTOM_MODEL) {
      onChange(''); // blank → text input appears
    } else {
      onChange(v);
    }
  };

  return (
    <>
      <select
        className={styles.select}
        value={isKnown ? value : CUSTOM_MODEL}
        onChange={e => handleSelect(e.target.value)}>
        {MODELS.map(m => (
          <option key={m.value} value={m.value}>{m.label}</option>
        ))}
        <option value={CUSTOM_MODEL}>Personalizado...</option>
      </select>
      {!isKnown && (
        <input
          className={styles.input}
          style={{ marginTop: 4 }}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="Ej: claude-opus-4-7"
          autoFocus
        />
      )}
    </>
  );
}
